import type { ModuleManifest } from '../types.js'

/*
 * ТҮГЭЭЛТ — автомашин, түгээлтийн баг, өдрийн явалт, захиалгын хуваарилалт.
 *
 * ! `warehouse`-ААС ТУСДАА байх шалтгаан: цэс нэг модулиар хүрээлэгддэг тул
 * ! агуулахын бэлдэгч түгээлтийн цэс харах шаардлагагүй, эсрэгээрээ ч мөн
 * ! адил ([[16-fulfilment-plan]] §Модулийн хуваарь).
 *
 * ! ГЭХДЭЭ өгөгдөл нь НЭГ: захиалгын гүйцэтгэлийн мөр (`wh_orders`) нь
 * ! агуулахын төлөв ба түгээлтийн хуваарилалт хоёуланг эзэмшинэ. Хоёр
 * ! хүснэгт болговол нэг захиалгын тухай хоёр үнэн үүснэ.
 */
export const deliveryModule = {
  key: 'delivery',
  name: 'Түгээлт',
  description: 'Автомашин, түгээлтийн баг, өдрийн явалт, захиалгын хуваарилалт.',
  version: '0.1.0',
  icon: 'TruckIcon',

  /*
   * ! ШИНЭ суулгацад УНТРААЛТТАЙ. Модуль хөгжүүлэлтийн шатанд байгаа —
   * ! бүрэн болтол нь бодит орчинд цэсэнд гарах ёсгүй.
   */
  enabledByDefault: false,

  permissions: [
    { resource: 'delivery.vehicles', action: 'list', description: 'Автомашин харах' },
    { resource: 'delivery.vehicles', action: 'manage', description: 'Автомашин бүртгэх, засах' },

    /*
     * ! БАГ нь ажилтны бүртгэл дээр суудаг. Түгээлтийн үүрэг оноох нь
     * ! мобайл аппын нэвтрэх эрхийг тодорхойлох тул `manage` эрх шаардана.
     */
    { resource: 'delivery.crew', action: 'list', description: 'Түгээлтийн баг харах' },
    { resource: 'delivery.crew', action: 'manage', description: 'Жолооч, түгээгч, хүргэгч оноох' },

    { resource: 'delivery.runs', action: 'list', description: 'Өдрийн түгээлт харах' },
    { resource: 'delivery.runs', action: 'manage', description: 'Түгээлт үүсгэх, машин нэмэх' },

    /*
     * ! ХУВААРИЛАЛТ нь тусдаа эрх. Машин бүртгэх хүн бүр захиалга ачих
     * ! шийдвэр гаргах ёсгүй — тэр нь өдөр тутмын үйл ажиллагааны шийдвэр.
     */
    { resource: 'delivery.assign', action: 'manage', description: 'Захиалгыг машинд хуваарилах' },

    /*
     * ! ХҮРГЭЛТ БҮРТГЭХ нь тусдаа эрх — түгээгчийн хийдэг үйлдэл. Машин
     * ! бүртгэх хүн хүргэлт баталгаажуулах эрхтэй байх шаардлагагүй.
     */
    { resource: 'delivery.record', action: 'manage', description: 'Хүргэлт баталгаажуулах' },

    /*
     * ! БОРЛУУЛАЛТ нь хүргэлт баталгаажуулахаас ТУСДАА эрх. Энэ үйлдэл нь 1С-д
     * ! САНХҮҮГИЙН баримт үүсгэж, харилцагчийн өрийг өөрчилдөг — «хүргэсэн гэж
     * ! тэмдэглэх» эрхтэй бүхэн падаан үүсгэж, мөнгө авч чаддаг байх ёсгүй.
     */
    { resource: 'delivery.sales', action: 'manage', description: 'Дэлгүүр дээр падаан үүсгэж, төлбөр авах' },

    { resource: 'delivery.returns', action: 'list', description: 'Буцаалт харах' },
    { resource: 'delivery.returns', action: 'manage', description: 'Буцаалтын хүсэлт үүсгэх' },

    /*
     * ! ХҮЛЭЭН АВАХ нь НЯРВЫН шийдвэр. Түгээгч өөрийн буцаалтаа хүлээн авах
     * ! боломжтой байвал агуулахын хяналт утгагүй болно.
     */
    { resource: 'delivery.returns', action: 'accept', description: 'Буцаалт хүлээн авах (нярав)' },
  ],

  navigation: [
    {
      label: 'Түгээлт',
      items: [
        {
          label: 'Самбар',
          href: '/delivery/dashboard',
          icon: 'LayoutDashboardIcon',
          iconClassName: 'text-primary',
          permission: 'delivery.runs:list',
        },
        {
          label: 'Өдрийн түгээлт',
          href: '/delivery/runs',
          icon: 'RouteIcon',
          iconClassName: 'text-sky-600 dark:text-sky-400',
          permission: 'delivery.runs:list',
        },
        {
          label: 'Автомашин',
          href: '/delivery/vehicles',
          icon: 'TruckIcon',
          iconClassName: 'text-emerald-600 dark:text-emerald-400',
          permission: 'delivery.vehicles:list',
        },
        {
          label: 'Буцаалт',
          href: '/delivery/returns',
          icon: 'Undo2Icon',
          iconClassName: 'text-amber-600 dark:text-amber-400',
          permission: 'delivery.returns:list',
        },
        {
          label: 'Түгээлтийн баг',
          href: '/delivery/crew',
          icon: 'UsersIcon',
          iconClassName: 'text-violet-600 dark:text-violet-400',
          permission: 'delivery.crew:list',
        },
      ],
    },
  ],

  resources: [
    { name: 'delivery.dashboard', label: 'Түгээлтийн самбар', list: '/delivery/dashboard', canDelete: false },
    { name: 'delivery.runs', label: 'Өдрийн түгээлт', list: '/delivery/runs', show: '/delivery/runs/:id', canDelete: false },
    { name: 'delivery.vehicles', label: 'Автомашин', list: '/delivery/vehicles', canDelete: true },
    { name: 'delivery.crew', label: 'Түгээлтийн баг', list: '/delivery/crew', canDelete: true },
    { name: 'delivery.returns', label: 'Буцаалт', list: '/delivery/returns', canDelete: false },
  ],
} as const satisfies ModuleManifest
